import { ACCOMMODATION_DOC_IPC_CHANNELS } from '../../../utils/constants';
import {
  ACCOMMODATION_PAYMENT_METHODS,
  AccommodationBooking,
  AccommodationPayment,
  AccommodationProperty,
} from '../types/accommodation';
import { isElectron } from '../util';
import { downloadPdfFromBase64 } from '../util/pdfDownload';
import { ClientOutstandingOrder } from './accommodationService';
import { supabase } from './supabase';

interface DocResult {
  success: boolean;
  cancelled?: boolean;
  filePath?: string;
  error?: string;
}

// ─── Build the common payload from Redux state ────────────────────────────────

function buildPayload(
  booking: AccommodationBooking,
  payments: AccommodationPayment[],
  property: AccommodationProperty,
  outstandingOrders?: ClientOutstandingOrder[],
) {
  return {
    property: {
      name: property.name,
      address: property.address ?? null,
      // phone: property.phone ?? null,
      // email: property.email ?? null,
      website: null,
      currency: property.currency ?? '',
      check_in_time: property.check_in_time ?? '14:00',
      check_out_time: property.check_out_time ?? '11:00',
    },
    booking: {
      id: booking.id,
      check_in: booking.check_in,
      check_out: booking.check_out,
      adults: booking.adults,
      children: booking.children,
      total_amount: booking.total_amount,
      amount_paid: booking.amount_paid ?? 0,
      currency: booking.currency ?? property.currency ?? '',
      notes: booking.notes ?? null,
      source_name:
        booking.booking_source?.name ??
        booking.source_name ??
        booking.source ??
        null,
      client: booking.client
        ? {
            first_name: booking.client.first_name,
            last_name: booking.client.last_name,
            email: booking.client.email ?? null,
            contact: booking.client.contact ?? null,
            address: booking.client.address ?? null,
            nationality: booking.client.nationality ?? null,
            id_type: booking.client.id_type ?? null,
            id_number: booking.client.id_number ?? null,
          }
        : null,
      rooms: (booking.booking_rooms ?? []).map((br) => ({
        room_number: br.room?.room_number ?? '—',
        room_type: br.room?.room_type?.name ?? '—',
        price_per_night: br.price_per_night,
        nights: br.nights,
        subtotal: br.subtotal,
      })),
      payments: payments.map((p) => {
        const method = ACCOMMODATION_PAYMENT_METHODS.find(
          (m) => m.id === p.payment_method_id,
        );
        return {
          amount: p.amount,
          payment_method_name: method?.name ?? `Method ${p.payment_method_id}`,
          paid_at: p.paid_at,
          recorded_by_name: null,
          notes: p.notes ?? null,
        };
      }),
    },
    outstandingOrders: (outstandingOrders ?? []).map((o) => ({
      bill_number: o.bill_number,
      sale_date: o.sale_date,
      table_name: o.table_name ?? null,
      section_name: o.section_name ?? null,
      amount: o.amount,
      payment_method_name: o.payment_method_name,
      notes: o.notes ?? null,
    })),
  };
}

// ─── Web export via edge function ─────────────────────────────────────────────

type DocPayload = ReturnType<typeof buildPayload>;

// ─── Exports ──────────────────────────────────────────────────────────────────

async function exportViaEdgeFunction(
  docType: 'confirmation' | 'checkin' | 'checkout',
  payload: DocPayload,
): Promise<DocResult> {
  const { data, error } = await supabase.functions.invoke(
    'export-accommodation-doc',
    { body: { docType, payload } },
  );
  if (error || !data?.pdfBase64) {
    return {
      success: false,
      error: error?.message ?? 'Failed to generate PDF',
    };
  }
  downloadPdfFromBase64(data.pdfBase64, data.filename);
  return { success: true };
}

export async function exportBookingConfirmationPdf(
  booking: AccommodationBooking,
  payments: AccommodationPayment[],
  property: AccommodationProperty,
): Promise<DocResult> {
  const payload = buildPayload(booking, payments, property);
  if (!isElectron()) {
    return exportViaEdgeFunction('confirmation', payload);
  }
  return window.electron.ipcRenderer.invoke(
    ACCOMMODATION_DOC_IPC_CHANNELS.EXPORT_BOOKING_CONFIRMATION_PDF,
    payload,
  ) as Promise<DocResult>;
}

export async function exportCheckinFormPdf(
  booking: AccommodationBooking,
  payments: AccommodationPayment[],
  property: AccommodationProperty,
): Promise<DocResult> {
  const payload = buildPayload(booking, payments, property);
  if (!isElectron()) {
    return exportViaEdgeFunction('checkin', payload);
  }
  return window.electron.ipcRenderer.invoke(
    ACCOMMODATION_DOC_IPC_CHANNELS.EXPORT_CHECKIN_FORM_PDF,
    payload,
  ) as Promise<DocResult>;
}

export async function exportCheckoutFormPdf(
  booking: AccommodationBooking,
  payments: AccommodationPayment[],
  property: AccommodationProperty,
  outstandingOrders: ClientOutstandingOrder[],
): Promise<DocResult> {
  const payload = buildPayload(booking, payments, property, outstandingOrders);
  if (!isElectron()) {
    return exportViaEdgeFunction('checkout', payload);
  }
  return window.electron.ipcRenderer.invoke(
    ACCOMMODATION_DOC_IPC_CHANNELS.EXPORT_CHECKOUT_FORM_PDF,
    payload,
  ) as Promise<DocResult>;
}
